/**
 * The two palettes from the canvas's `renderVals()`, verbatim.
 *
 * The canvas resolves eleven custom properties per theme and hands them to the
 * root element, so every section below reads `var(--ink)` and never learns
 * which theme is active. Keeping that indirection is what makes the theme
 * toggle a one-line state change instead of a prop threaded through ten files.
 *
 * The derived values below the palettes are the canvas's own: they cross-fade
 * the hero video out and the dark-mode starfield in, rather than swapping
 * either abruptly. `heroVideoVis` exists alongside `heroVideoOpacity` because
 * opacity alone leaves the video decoding behind a transparent layer.
 */

export type ThemeName = "light" | "dark";

export interface Palette {
  paper: string;
  paper2: string;
  ink: string;
  inkRgb: string;
  chip: string;
  chipfg: string;
  btn: string;
  btnfg: string;
  link: string;
  linkh: string;
  acc2: string;
  /**
   * The ground under the three dark band sections (Platform, Agents, Cta).
   *
   * Those sections hard-coded `#101216` for both themes, which is a light-mode
   * decision that was never re-made for dark. Against light paper (#F6F3EC)
   * that value is a dramatic near-black slab and the rhythm works. Against
   * dark paper (#0A0C0F) it is six RGB units LIGHTER, which is too small to
   * read as a designed plane and too large to disappear: it lands as a seam.
   * It also inverts the meaning, because the same band recedes in light and
   * advances in dark.
   *
   * So the band recedes in both themes rather than inverting between them,
   * and it is a real plane in both: cream-deepened in light, a couple of units
   * under paper in dark. It used to be near-black in BOTH, which meant light
   * mode had no light state across a third of the page. Everything that sits
   * on it resolves from the `band*` tokens below for the same reason.
   */
  band: string;
  /** Primary text on the band. */
  bandInk: string;
  /** The same, as an RGB triplet, for the rgba() text ramp. */
  bandInkRgb: string;
  /** Hairlines and low-alpha fills on the band. */
  bandLineRgb: string;
  /** A surface raised ABOVE the band, e.g. the agent panel's inner card. */
  bandSurface: string;
  /** Accent used for TEXT on the band. Darker in light for contrast. */
  bandAcc: string;
  /** Accent used for FILLS and borders on the band, as an RGB triplet. */
  bandAccRgb: string;
  /** Growth and success figures. Literal #3DDC97 only worked on dark. */
  pos: string;
  /** acc2 and pos as RGB triplets, for the rgba() tints ported pages use. */
  acc2Rgb: string;
  posRgb: string;
  /** Text sitting on a solid accent fill. */
  bandOnAcc: string;
  /** Hover state for band accent text. Brightens in both themes. */
  bandAccH: string;
  /**
   * The footer's ground, one step below the band.
   *
   * Completes a recession ladder the page can be read down: paper, then
   * band, then footer, each a little further back. It was #0B0D10 in both
   * themes, which made it the last place on a light page with no light
   * state -- the same fault the bands had, surviving one level lower.
   */
  footer: string;
}

export const PALETTES: Record<ThemeName, Palette> = {
  light: {
    paper: "#F6F3EC",
    paper2: "#FCFAF4",
    ink: "#14130F",
    inkRgb: "20,19,15",
    chip: "#14130F",
    chipfg: "#F6F3EC",
    btn: "#14130F",
    btnfg: "#F6F3EC",
    link: "#087585",
    linkh: "#0A5560",
    // This carries BOTH large display text (the hero accent, the CEO figures)
    // and small text (the accordion toggles, the dashboard chips), so it has
    // to clear 4.5 rather than the 3.0 large text alone would need. It was
    // #0AA6C0 at 2.62:1; this measures 4.90 on paper and 5.03 on paper2.
    // It equals --link now, which dark already did -- both are #38E5FF there.
    acc2: "#087585",
    band: "#ECE8DF",
    bandInk: "#14130F",
    bandInkRgb: "20,19,15",
    bandLineRgb: "20,19,15",
    bandSurface: "#F6F3EC",
    // #0A5560, not the brighter #0AA6C0: teal-on-cream measures 2.38:1, which
    // fails for the small-caps eyebrows this colours. This one is 6.88:1.
    bandAcc: "#0A5560",
    // The brighter teal survives as a FILL, where nothing has to be read
    // through it. Solid, it carries #14130F text at 6.46:1.
    bandAccRgb: "10,166,192",
    pos: "#0B7A4B",
    acc2Rgb: "8,117,133",
    posRgb: "11,122,75",
    bandOnAcc: "#14130F",
    bandAccH: "#087585",
    footer: "#E4DFD4",
  },
  dark: {
    paper: "#0A0C0F",
    paper2: "#161920",
    ink: "#F2F4F6",
    inkRgb: "236,239,242",
    chip: "#1E222A",
    chipfg: "#F2F4F6",
    btn: "#F2F4F6",
    btnfg: "#101216",
    link: "#38E5FF",
    linkh: "#9DF1FF",
    acc2: "#38E5FF",
    band: "#080A0D",
    bandInk: "#F2F4F6",
    bandInkRgb: "242,244,246",
    bandLineRgb: "255,255,255",
    bandSurface: "#15181D",
    bandAcc: "#38E5FF",
    bandAccRgb: "56,229,255",
    pos: "#3DDC97",
    acc2Rgb: "56,229,255",
    posRgb: "61,220,151",
    bandOnAcc: "#101216",
    bandAccH: "#9DF1FF",
    footer: "#05070A",
  },
};

/** Where the canvas persists the visitor's choice. Key kept byte-identical. */
export const THEME_STORAGE_KEY = "ai2aim-theme";

export interface ThemeVals {
  heroVideoOpacity: string;
  heroVideoVis: "visible" | "hidden";
  darkFxOpacity: string;
  darkFxVis: "visible" | "hidden";
  heroGridOpacity: string;
  themeIcon: string;
  themeLabel: string;
}

export function themeVals(theme: ThemeName): ThemeVals {
  const dark = theme === "dark";
  return {
    heroVideoOpacity: dark ? "0" : "1",
    heroVideoVis: dark ? "hidden" : "visible",
    darkFxOpacity: dark ? "1" : "0",
    darkFxVis: dark ? "visible" : "hidden",
    heroGridOpacity: dark ? ".45" : "0",
    themeIcon: dark ? "\u2600" : "\u263E",
    themeLabel: dark ? "Switch to light mode" : "Switch to dark mode",
  };
}

/* ── Bridging the shadcn ramp ────────────────────────────────────────────────
   The same fault T091726008 fixed for `--landing-*`, one level up and with a
   wider blast radius.

   Tailwind resolves `text-foreground`, `bg-card`, `text-muted-foreground` and
   the rest through `hsl(var(--foreground))`, and global.css defines those on
   `:root` and redefines them under the `.dark` CLASS. This canvas themes a div
   and never sets that class, so on a DARK canvas page every one of them stayed
   on its LIGHT value: `--foreground` resolved to #012556, a dark navy, on a
   near-black ground, and `--muted-foreground` to #48607F.

   That is why the comment tree, the reset page and the 404 were unreadable in
   dark while everything around them was fine — they are built from shadcn
   primitives rather than canvas tokens.

   Redefining the ramp here fixes every such component at once, without any of
   them learning which design system they are standing in. The values are
   DERIVED from the canvas palette rather than restated, so the two cannot
   drift: there is one definition of ink and one of paper, and these are
   mixtures of them. */

/** "#F6F3EC" -> [246, 243, 236] */
function rgbOf(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [
    parseInt(h.slice(0, 2), 16),
    parseInt(h.slice(2, 4), 16),
    parseInt(h.slice(4, 6), 16),
  ];
}

/** `t` of `a` over `b`, as hex. Used to derive the muted and border steps. */
function mix(a: string, b: string, t: number): string {
  const [ar, ag, ab] = rgbOf(a);
  const [br, bg, bb] = rgbOf(b);
  const c = (x: number, y: number) =>
    Math.round(x * t + y * (1 - t))
      .toString(16)
      .padStart(2, "0");
  return `#${c(ar, br)}${c(ag, bg)}${c(ab, bb)}`;
}

/**
 * "#F6F3EC" -> "40 43% 95%".
 *
 * The triplet form, without the `hsl()` wrapper, because that is how Tailwind
 * consumes these: `hsl(var(--foreground))`. Handing it a hex would produce
 * `hsl(#14130F)`, which is invalid and silently drops the colour.
 */
function hsl(hex: string): string {
  const [r8, g8, b8] = rgbOf(hex);
  const r = r8 / 255;
  const g = g8 / 255;
  const b = b8 / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let sat = 0;
  if (d !== 0) {
    sat = d / (1 - Math.abs(2 * l - 1));
    if (max === r) h = ((g - b) / d) % 6;
    else if (max === g) h = (b - r) / d + 2;
    else h = (r - g) / d + 4;
    h *= 60;
    if (h < 0) h += 360;
  }
  return `${Math.round(h)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

/** The shadcn ramp, derived from one palette. */
function shadcnVars(p: Palette): Record<string, string> {
  const ink = p.ink;
  const paper = p.paper;
  return {
    "--background": hsl(paper),
    "--foreground": hsl(ink),
    "--card": hsl(p.paper2),
    "--card-foreground": hsl(ink),
    "--popover": hsl(p.paper2),
    "--popover-foreground": hsl(ink),
    "--muted": hsl(mix(ink, paper, 0.06)),
    /* .62 is the floor solved for elsewhere in this file: ink at .58 over light
       paper measures 4.46:1 and at .62 measures 5.08:1. */
    "--muted-foreground": hsl(mix(ink, paper, 0.62)),
    "--secondary": hsl(mix(ink, paper, 0.06)),
    "--secondary-foreground": hsl(ink),
    "--accent": hsl(mix(ink, paper, 0.06)),
    "--accent-foreground": hsl(ink),
    "--border": hsl(mix(ink, paper, 0.13)),
    "--input": hsl(mix(ink, paper, 0.13)),
    "--primary": hsl(p.acc2),
    "--primary-foreground": hsl(p.btnfg),
    "--ring": hsl(p.acc2),
  };
}

/** The eleven custom properties, as a style object for the root element. */
export function paletteVars(theme: ThemeName): React.CSSProperties {
  const p = PALETTES[theme];
  return {
    "--paper": p.paper,
    "--paper2": p.paper2,
    "--ink": p.ink,
    "--ink-rgb": p.inkRgb,
    "--chip": p.chip,
    "--chipfg": p.chipfg,
    "--btn": p.btn,
    "--btnfg": p.btnfg,
    "--link": p.link,
    "--linkh": p.linkh,
    "--acc2": p.acc2,
    "--band": p.band,
    "--band-ink": p.bandInk,
    "--band-ink-rgb": p.bandInkRgb,
    "--band-line-rgb": p.bandLineRgb,
    "--band-surface": p.bandSurface,
    "--band-acc": p.bandAcc,
    "--band-acc-rgb": p.bandAccRgb,
    "--pos": p.pos,
    "--acc2-rgb": p.acc2Rgb,
    "--pos-rgb": p.posRgb,
    "--band-on-acc": p.bandOnAcc,
    "--band-acc-h": p.bandAccH,
    "--footer": p.footer,

    /* How far a band section takes to cross into paper.

       This used to be 0 in light, because the band was near-black against
       cream and that hard edge was the design. The band now has a real light
       state, so in neither theme is the step large enough to carry an abrupt
       boundary, and both cross over the ramp. */
    "--dc-seam-h": "130px",

    /* ── Bridge to the --landing-* text ramp (T091726008) ─────────────────
       Shared components from components/forms colour themselves with
       `--landing-text` and friends, which resolve through `--tx` in
       global.css. Those flip on the `.dark` CLASS, set by
       components/common/ThemeToggle — the pre-canvas theme system. This
       canvas has its own theme under `ai2aim-theme` and never renders that
       toggle, so on a dcv2 page in dark mode `--tx` stayed at its light value
       (#012556, dark navy) and every field label in the forum's Create
       Subforum dialog painted dark navy on a dark panel: invisible.

       Rather than teach one dialog to use canvas tokens, the ramp is
       redefined here for anything rendered inside the canvas. Any shared
       component that lands on a dcv2 page is now legible in both themes
       without knowing which design system it is standing in. The same split
       caused the favicon never to swap on these pages; this is that bug's
       other half. */
    "--landing-text": p.ink,
    "--landing-text-secondary": `rgba(${p.inkRgb},.78)`,
    "--landing-text-muted": `rgba(${p.inkRgb},.62)`,
    "--landing-text-dim": `rgba(${p.inkRgb},.45)`,
    "--landing-surface": p.paper2,
    "--landing-glass-border": `rgba(${p.inkRgb},.16)`,

    ...shadcnVars(p),

    /* The rest of that ramp, added when ten routed pages moved onto this
       canvas. They were built on the pre-canvas shell and read fifteen
       --landing-* properties; six were bridged above for one dialog's labels,
       and the other nine still resolved from global.css, which flips on the
       `.dark` CLASS this canvas never sets. So on a light canvas page they
       kept a dark card fill, a dark grid and a glow multiplier meant for a
       dark ground.

       Completing the ramp is what let those pages port by swapping their shell
       rather than by rewriting every surface in them. Each maps to the canvas
       equivalent rather than to a lookalike: one ground, one raised surface,
       one hairline. */
    "--landing-bg": p.paper,
    "--landing-card-bg": p.paper2,
    /* Flat. The pre-canvas world gave cards a gradient fill; this one does
       not, and a gradient here would be the single card on the site wearing
       a different material. */
    "--landing-card-bg-gradient": p.paper2,
    "--landing-glass-bg": p.paper2,
    "--landing-card-border": `rgba(${p.inkRgb},.13)`,
    /* Offset and blur, never a zero-offset halo. Suppressed in dark, where a
       shadow on near-black ground reads as smudge rather than elevation --
       the same call the original ramp made. */
    "--landing-card-shadow":
      theme === "dark" ? "none" : `0 1px 2px rgba(${p.inkRgb},.05), 0 8px 24px rgba(${p.inkRgb},.06)`,
    "--landing-surface-hover": `rgba(${p.inkRgb},.06)`,
    "--landing-grid-line": `rgba(${p.inkRgb},.06)`,
    /* Zero. This multiplier scaled decorative glows that no longer exist on
       the canvas -- the per-section blooms came out entirely. Leaving it at
       the old 0.55/1 would light pages the rest of the site does not. */
    "--landing-glow-mult": "0",
  } as React.CSSProperties;
}

/** Reads the persisted theme. Returns null when storage is unavailable. */
export function readStoredTheme(): ThemeName | null {
  try {
    const v = localStorage.getItem(THEME_STORAGE_KEY);
    return v === "light" || v === "dark" ? v : null;
  } catch {
    return null;
  }
}

export function writeStoredTheme(theme: ThemeName): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    /* Private browsing and blocked storage both land here; the toggle still
       works for the session, it just will not survive a reload. */
  }
}
